import {
  Bound,
  clamp,
  type SerializedXYWH,
} from '@blocksuite/affine/global/gfx';
import {
  getGfxBlockElementModelSize,
  toGfxBlockComponent,
} from '@blocksuite/affine/std';
import {
  EDGELESS_CANVAS_CARD_BACKGROUND,
  EDGELESS_CANVAS_CARD_SHADOW,
} from '@blocksuite/affine-shared/consts';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { html, type PropertyValues } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import {
  claimActiveAIChatBlockAutoSize,
  completeActiveAIChatBlockAutoSize,
  releaseActiveAIChatBlockAutoSize,
} from './active-auto-size';
import { AIChatBlockComponent } from './ai-chat-block';
import { AIChatBlockSchema } from './model';

const AI_CHAT_BLOCK_MIN_CONTENT_HEIGHT = 220;
const AI_CHAT_BLOCK_VERTICAL_PADDING = 32;
const AI_CHAT_BLOCK_FINAL_SIZE_SETTLE_MS = 500;

export class EdgelessAIChatBlockComponent extends toGfxBlockComponent(
  AIChatBlockComponent
) {
  private readonly _autoSizeOwner = Symbol('ai-chat-active-auto-size');

  private _autoSizeClaimed = false;

  private _autoSizedTransientXYWH: SerializedXYWH | null = null;

  private _contentResizeObserver: ResizeObserver | null = null;

  private _observedContent: HTMLElement | null = null;

  private _finalSizeTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.borderRadius = '8px';
    this.style.boxShadow = EDGELESS_CANVAS_CARD_SHADOW;
  }

  override disconnectedCallback(): void {
    this._disconnectActiveAutoSize();
    super.disconnectedCallback();
  }

  protected override firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);
    void this._syncActiveAutoSize();
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    void this._syncActiveAutoSize();
  }

  private async _syncActiveAutoSize() {
    if (!this._autoSizeClaimed) {
      this._autoSizeClaimed = claimActiveAIChatBlockAutoSize(
        this.model.id,
        this._autoSizeOwner
      );
    }
    if (!this._autoSizeClaimed) return;

    const messages = this.renderRoot.querySelector('ai-chat-block-messages');
    if (!messages) return;
    await messages.updateComplete;
    if (!this.isConnected || !this._autoSizeClaimed) return;

    const content =
      messages.shadowRoot?.querySelector<HTMLElement>('.ai-chat-messages');
    if (!content) return;

    if (content !== this._observedContent) {
      this._contentResizeObserver?.disconnect();
      this._contentResizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) this._measureActiveContent(content, entry);
      });
      this._contentResizeObserver.observe(content);
      this._observedContent = content;
    }

    this._measureActiveContent(content);
  }

  private _measureActiveContent(
    content: HTMLElement,
    resizeObserverEntry?: ResizeObserverEntry
  ) {
    if (!this._autoSizeClaimed || this.store.readonly) return;

    const measured = getGfxBlockElementModelSize(content, {
      resizeObserverEntry,
    });
    if (!measured.height) return;

    const scale = this.model.props.scale$.peek();
    const bound = Bound.deserialize(this.model.props.xywh$.peek());
    const generationState = this.model.props.generationState$.peek();
    const generatingHeightFloor =
      generationState === 'generating' ? bound.h / scale : 0;
    const renderedHeight = Math.max(
      AI_CHAT_BLOCK_MIN_CONTENT_HEIGHT,
      generatingHeightFloor,
      Math.ceil(measured.height + AI_CHAT_BLOCK_VERTICAL_PADDING)
    );
    const targetHeight = renderedHeight * scale;

    if (Math.abs(bound.h - targetHeight) <= 1) {
      this._clearAutoSizedTransientGeometry();
    } else {
      bound.h = targetHeight;
      this._setAutoSizedTransientGeometry(bound.serialize());
    }

    if (generationState !== 'generating') {
      this._scheduleFinalSizeWrite(content);
    }
  }

  private _scheduleFinalSizeWrite(content: HTMLElement) {
    if (this._finalSizeTimer !== null) {
      window.clearTimeout(this._finalSizeTimer);
    }

    this._finalSizeTimer = window.setTimeout(() => {
      this._finalSizeTimer = null;
      const hasPendingImages = [...content.querySelectorAll('img')].some(
        image => !image.complete
      );
      if (hasPendingImages || document.fonts?.status === 'loading') {
        this._scheduleFinalSizeWrite(content);
        return;
      }
      this._persistFinalActiveHeight();
    }, AI_CHAT_BLOCK_FINAL_SIZE_SETTLE_MS);
  }

  private _persistFinalActiveHeight() {
    if (!this._autoSizeClaimed || this.store.readonly) return;

    const transientXYWH = this._autoSizedTransientXYWH;
    if (transientXYWH) {
      const persisted = Bound.deserialize(this.model.props.xywh$.peek());
      const measured = Bound.deserialize(transientXYWH);
      if (Math.abs(persisted.h - measured.h) > 1) {
        persisted.h = measured.h;
        // Exactly one Yjs geometry write, owned by the locally created turn.
        // Reload/hydration never registers an owner and cannot reach this path.
        this.store.updateBlock(this.model, { xywh: persisted.serialize() });
      }
    }

    completeActiveAIChatBlockAutoSize(this.model.id, this._autoSizeOwner);
    this._autoSizeClaimed = false;
    this._clearAutoSizedTransientGeometry();
    this._contentResizeObserver?.disconnect();
    this._contentResizeObserver = null;
    this._observedContent = null;
  }

  private _setAutoSizedTransientGeometry(xywh: SerializedXYWH) {
    if (this._autoSizedTransientXYWH === xywh) return;
    this._autoSizedTransientXYWH = xywh;
    this.model.transientXYWH = xywh;
    this.gfx.grid.update(this.model);
    this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
    this.requestUpdate();
  }

  private _clearAutoSizedTransientGeometry() {
    if (
      this._autoSizedTransientXYWH &&
      this.model.transientXYWH === this._autoSizedTransientXYWH
    ) {
      this.model.transientXYWH = undefined;
      this.gfx.grid.update(this.model);
      this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
      this.requestUpdate();
    }
    this._autoSizedTransientXYWH = null;
  }

  private _disconnectActiveAutoSize() {
    this._contentResizeObserver?.disconnect();
    this._contentResizeObserver = null;
    this._observedContent = null;
    if (this._finalSizeTimer !== null) {
      window.clearTimeout(this._finalSizeTimer);
      this._finalSizeTimer = null;
    }
    this._clearAutoSizedTransientGeometry();
    if (this._autoSizeClaimed) {
      releaseActiveAIChatBlockAutoSize(this.model.id, this._autoSizeOwner);
      this._autoSizeClaimed = false;
    }
  }

  override renderGfxBlock() {
    const bound = Bound.deserialize(
      this.model.transientXYWH ?? this.model.props.xywh$.value
    );
    const scale = this.model.props.scale$.value;
    const width = bound.w / scale;
    const height = bound.h / scale;
    const style = {
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: '8px',
      background: EDGELESS_CANVAS_CARD_BACKGROUND,
      transformOrigin: '0 0',
      transform: `scale(${scale})`,
    };

    return html`
      <div class="edgeless-ai-chat" style=${styleMap(style)}>
        ${this.renderPageContent()}
      </div>
    `;
  }
}

export const EdgelessAIChatBlockInteraction =
  GfxViewInteractionExtension<EdgelessAIChatBlockComponent>(
    AIChatBlockSchema.model.flavour,
    {
      selectionBorderRadius: 8,
      resizeConstraint: {
        minWidth: 320,
        minHeight: 220,
        maxWidth: 640,
        maxHeight: 800,
      },

      handleRotate() {
        return {
          beforeRotate(context) {
            context.set({
              rotatable: false,
            });
          },
        };
      },

      handleResize({ model }) {
        const initialScale = model.props.scale$.peek();

        return {
          onResizeStart(context) {
            context.default(context);
            model.stash('scale');
          },

          onResizeMove(context) {
            if (model.store.readonly) return;

            const { newBound, originalBound, lockRatio, constraint } = context;
            const { minWidth, maxWidth, minHeight, maxHeight } = constraint;

            let scale = initialScale;
            const originalRealWidth = originalBound.w / scale;

            // update scale if resize is proportional
            if (lockRatio) {
              scale = newBound.w / originalRealWidth;
            }

            let newRealWidth = clamp(newBound.w / scale, minWidth, maxWidth);
            let newRealHeight = clamp(newBound.h / scale, minHeight, maxHeight);

            newBound.w = newRealWidth * scale;
            newBound.h = newRealHeight * scale;

            model.props.xywh = newBound.serialize();
            if (scale !== initialScale) {
              model.props.scale = scale;
            }
          },

          onResizeEnd(context) {
            context.default(context);
            model.pop('scale');
          },
        };
      },
    }
  );

declare global {
  interface HTMLElementTagNameMap {
    'affine-edgeless-ai-chat': EdgelessAIChatBlockComponent;
  }
}
